import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import interfaceApi from '@/api/admin/interfaceApi'

export interface InterfaceByOrganization {
  id: number
  name: string
  customName: string
  status: boolean
  state: string
}

export interface InterfaceByOrganizationState {
  data: InterfaceByOrganization[]
  isLoading: boolean
  error: string | null
  lastFetched: number | null
}

const initialState: InterfaceByOrganizationState = {
  data: [],
  isLoading: false,
  error: null,
  lastFetched: null,
}

export const fetchInterfacesByOrganization = createAsyncThunk(
  'interfaceByOrganization/fetchByOrganization',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await interfaceApi.getMyInterface()
      return data as InterfaceByOrganization[]
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch interfaces by organization',
      )
    }
  },
)

const interfaceByOrganizationSlice = createSlice({
  name: 'interfaceByOrganization',
  initialState,
  reducers: {
    clearInterfacesByOrganization: (state) => {
      state.data = []
      state.error = null
      state.lastFetched = null
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    updateInterfaceStatus: (
      state,
      action: PayloadAction<{ id: number; status: boolean }>,
    ) => {
      const { id, status } = action.payload
      const interface_ = state.data.find((item) => item.id === id)
      if (interface_) {
        interface_.status = status
      }
    },
    updateInterfaceState: (
      state,
      action: PayloadAction<{ id: number; state: string }>,
    ) => {
      const { id, state: interfaceState } = action.payload
      const interface_ = state.data.find((item) => item.id === id)
      if (interface_) {
        interface_.state = interfaceState
      }
    },
    setInterfacesByOrganization: (
      state,
      action: PayloadAction<InterfaceByOrganization[]>,
    ) => {
      state.data = action.payload
      state.error = null
      state.lastFetched = Date.now()
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInterfacesByOrganization.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchInterfacesByOrganization.fulfilled, (state, action) => {
        state.isLoading = false
        state.data = action.payload
        state.lastFetched = Date.now()
        state.error = null
      })
      .addCase(fetchInterfacesByOrganization.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const {
  clearInterfacesByOrganization,
  setError,
  updateInterfaceStatus,
  updateInterfaceState,
  setInterfacesByOrganization,
} = interfaceByOrganizationSlice.actions

export const selectInterfacesByOrganization = (state: {
  interfaceByOrganization: InterfaceByOrganizationState
}) => state.interfaceByOrganization.data

export const selectInterfacesByOrganizationLoading = (state: {
  interfaceByOrganization: InterfaceByOrganizationState
}) => state.interfaceByOrganization.isLoading

export const selectInterfacesByOrganizationError = (state: {
  interfaceByOrganization: InterfaceByOrganizationState
}) => state.interfaceByOrganization.error

export const selectInterfacesByOrganizationLastFetched = (state: {
  interfaceByOrganization: InterfaceByOrganizationState
}) => state.interfaceByOrganization.lastFetched

export const selectInterfaceByOrganizationById = (
  state: { interfaceByOrganization: InterfaceByOrganizationState },
  id: number,
) => state.interfaceByOrganization.data.find((item) => item.id === id)

export const selectActiveInterfacesByOrganization = (state: {
  interfaceByOrganization: InterfaceByOrganizationState
}) => state.interfaceByOrganization.data.filter((item) => item.status === true)

export const selectOnlineInterfacesByOrganization = (state: {
  interfaceByOrganization: InterfaceByOrganizationState
}) =>
  state.interfaceByOrganization.data.filter((item) => item.state === 'Online')

export const selectInterfacesByOrganizationCount = (state: {
  interfaceByOrganization: InterfaceByOrganizationState
}) => state.interfaceByOrganization.data.length

export const selectActiveInterfacesByOrganizationCount = (state: {
  interfaceByOrganization: InterfaceByOrganizationState
}) =>
  state.interfaceByOrganization.data.filter((item) => item.status === true)
    .length

export const selectOnlineInterfacesByOrganizationCount = (state: {
  interfaceByOrganization: InterfaceByOrganizationState
}) =>
  state.interfaceByOrganization.data.filter((item) => item.state === 'Online')
    .length

export default interfaceByOrganizationSlice.reducer
